import { useThrottle } from '@linked/controls';
import { Chip, alpha, IconButton, InputBase, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import { AttributesMixin, define, Linked, Model, type } from '@type-r/models';
import { useBoundLink, useLinked, useModel } from '@type-r/react';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { div } from '../common';
import { Flex } from '../flex';
import { asDate } from '../formatters';
import { CompactInput } from './compact';
import { FormSelect } from './horizontal';
import { Input } from './inputs';
import { Option } from './select';
import { SelectMulti } from './selecti-multi';

/**
 * **SearchInput** is an special text input with search icon and throttled update of the enclosed value.
 * It updates the linked value after 1 second when user stopped typing.
 *
 * This input can be used to make an instant search and filtering on linked value change.
 */
export const SearchInput = ({ $value, ...props }) => {
    const classes = useStyles();
    const $inputValue = useBoundLink( $value )
        .onChange(
            useThrottle(
                x => { $value.set( x.trim() ) },
                1000,
                [ $value.value ]
            )
        );

    return (
        <div className={classes.search}>
            <div className={classes.searchIcon}>
                <SearchIcon />
            </div>
            <InputBase
                placeholder="Search…"
                classes={{
                    root: classes.inputRoot,
                    input: classes.inputInput,
                }}
                inputProps={{ 'aria-label': 'search' }}
                { ...$inputValue.props }
                { ...props }
            />
        </div>
    )
}

const useStyles = makeStyles( theme => ({
    inputRoot: {
        color: 'inherit',
        width: '100%'
      },
    inputInput: {
        paddingLeft: 24,
        transition: theme.transitions.create('width'),
        width: '100%',
        fontSize: 14
    },
    search: {
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: alpha(theme.palette.common.white, 0.15),
        '&:hover': {
          backgroundColor: alpha(theme.palette.common.white, 0.25),
        },
        marginLeft: 0,
        width: '100%'
      },
      searchIcon: {
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      chip: {
        marginRight: 4,
        marginBottom: 4,
        overflow: 'hidden',
      },
      formSelect: {
        maxWidth: 250
      }
}))

export const FiltersList = ({ filter } : {
    filter : Model
}) => {
    const labels = useLabels( filter );
    const classes = useStyles();

    return (
        <FiltersContainer>
            { filter.keys().map( key => {
                if( key !== 'id' && key !== 'filterAll' ){
                    const value = filter[ key ] as FilterModel;
                    const isValid = value && ( value.isValid ? value.isValid() : true );
                    const label = isValid && value.asLabel();

                    return isValid ?
                        <Chip
                            label={ label === undefined ? labels[ key ] : labels[ key ] + ': ' + label }
                            onDelete={ () => value.delete() }
                            title={value.asTitle()}
                            className={classes.chip}
                        />
                        : null;
                }
            })}
        </FiltersContainer>
    )
}

const FiltersContainer = div({
    display: 'flex',
    flexDirection : 'row',
    flexWrap: 'wrap',
    width: '100%',
})

const isValid = value => value && ( value.isValid ? value.isValid() : true );

function useLabels( filter : Model ){
    const { displayLabels } = filter.constructor as any;
    return useMemo( () =>
        filter.mapObject( ( _, key ) => {
            const displayLabel =  displayLabels && displayLabels[ key ];
            if( displayLabel ) return displayLabel;

            const x = key.replace( /([A-Z])/g, " $1" );
            return x.charAt(0).toUpperCase() + x.slice( 1 );
        })
    , [ filter.constructor ]);
}

export const AddFilter = ({ filter, filterAllPlaceholder } : {
    filter : Model,
    filterAllPlaceholder? : String
}) => {
    const labels = useLabels( filter );
    const classes = useStyles();

    const emptyFilters = useMemo( () => {
        const emptyFilters = ['filterAll'];

        for( let key in filter.attributes ){
            if( key !== 'id' && key !== 'filterAll' ){
                if( !isValid(filter[ key ]) && !filter[key].isInitial ){
                    emptyFilters.push( key );
                }
            }
        }

        return emptyFilters;
    }, [ filter[ '_changeToken' ] ]);

    const $filterBy = useLinked( emptyFilters[ 0 ] );

    useEffect( () => {
        if( ! emptyFilters.includes( $filterBy.value ) ){
            $filterBy.set( emptyFilters[ 0 ] );
        }
    }, [ emptyFilters ])
    if( !$filterBy.value ){
        return <div/>
    }

    const Attr = filter[ '_attributes' ][ $filterBy.value ];

    const EnterAttr : any =
        Attr.type === DateRangeFilter ? EnterRange :
        Attr.type === MultiSelectFilter ? EnterMultiSelect :
        Attr.type === BooleanFilter ? EnterBoolean :
            EnterText;

    const filterAll = $filterBy.value === 'filterAll';

    return (
        <Flex row>
            <FormSelect $value={ $filterBy } classes={{
                    root: classes.formSelect
                }}>
                { emptyFilters.map( key =>
                    <Option value={ key } key={key}>
                        { labels[ key ] || Attr.name }
                    </Option>
                )}
            </FormSelect>

            { filterAll ?
                <SearchInput
                    $value={ (filter as any).$.filterAll }
                    placeholder={ filterAllPlaceholder || "Filter by all fields" }
                />
            :
                <EnterAttr key={$filterBy.value} filterValue={filter[$filterBy.value]}
                    onEnter={
                        range => filter[ $filterBy.value ] = range
                    }
                />
            }
        </Flex>
    )
}

export const EnterText = ({ onEnter }) => {
    const state = useModel(TextFilter);

    return <>
        <Input $value={ state.$.value }
                placeholder="Add filter"
                onKeyPress={ e => {
                    if( e.charCode === 13 ){
                        onEnter( state.clone() );
                    }
                }}
        />
        <IconButton
            aria-label="Add"
            size="small"
            onClick={ () => onEnter( state.clone() )}
        >
            <AddIcon />
        </IconButton>
    </>
}

export const EnterBoolean = ({ onEnter }) => {
    const state = useModel(BooleanFilter);

    return <>
        <IconButton
            aria-label="Add"
            size="small"
            onClick={ () => {
                state.$.value.true();
                onEnter( state.clone() );
            } }
        >
            <AddIcon />
        </IconButton>
    </>;
}

export abstract class FilterModel extends Model {
    static attributes = {
        isInitial : type(Boolean).value(false),
    };
    abstract delete();
    abstract asLabel();

    asTitle() {
        return this.asLabel();
    }
}

export interface TextFilter extends AttributesMixin<typeof TextFilter>{}
@define
export class TextFilter extends FilterModel {
    static attributes = {
        ...FilterModel.attributes,
        value : type(String).required
    };

    delete() {
        this.value = undefined;
    }

    asLabel() {
        return this.value;
    }
}

export interface BooleanFilter extends AttributesMixin<typeof BooleanFilter>{}
@define
export class BooleanFilter extends FilterModel {
    static attributes = {
        ...FilterModel.attributes,
        value: type(Boolean).required
    };

    delete() {
        this.value = undefined;
    }

    asLabel() {
        return undefined;
    }
}

@define
export class DateRangeFilter extends FilterModel {
    static attributes = {
        ...FilterModel.attributes,
        from : type( Date ).null.required,
        to : type( Date ).null.required
    }

    delete() {
        this.from = undefined;
        this.to = undefined;
    }

    asLabel() {
        return asDate(this.from) + ' - ' + asDate((this.to));
    }
}

export interface DateRangeFilter extends AttributesMixin<typeof DateRangeFilter>{}

export const SelectDateRange = ({ model, $from, $to } : {
    model? : DateRangeFilter
    $from?: Linked<Date>
    $to?: Linked<Date>
}) =>{

  const $start = model? model.$.from : $from;
  const $end = model? model.$.to : $to;

  return (

    <>
      <Input $value={ $start }
          date
          placeholder="From"
          selectsStart
          endDate={$end.value}
      />

      <Input $value={ $end}
          date
          placeholder="To"
          selectsEnd
          startDate={$start.value}
          endDate={$end.value}
          minDate={$start.value}
      />
   </>
  )
}


export const CompactSelectDateRange = ({ fromLabel, $from, toLabel, $to } : {
  fromLabel: React.ReactNode
  $from: Linked<Date>
  toLabel: React.ReactNode
  $to: Linked<Date>
}) =>
  <>
    <CompactInput label={fromLabel} $value={ $from }
        date
        placeholder="From"
        selectsStart
        endDate={$to.value}
    />

    <CompactInput label={toLabel} $value={ $to}
        date
        placeholder="To"
        selectsEnd
        startDate={$from.value}
        endDate={$to.value}
        minDate={$from.value}
    />
 </>



export const EnterRange = ({ onEnter }) => {
    const state = useModel( DateRangeFilter );

    React.useEffect( () => {
        if( state.isValid() ){
            onEnter({ from : state.from, to : state.to.setUTCHours(23,59,59,999) });
        }
    }, [ state[ '_changeToken' ] ]);

    return (
        <SelectDateRange model={ state } />
    )
}

export const EnterMultiSelect = ({ onEnter, filterValue }) => {

    return (
        <SelectMulti
            options={filterValue.options}
            $selected={filterValue.$.selected}
            onMenuClosed={() => {
                if (filterValue.selected && filterValue.selected.length) {
                    filterValue.$.isDone.set(true);
                    onEnter(filterValue.clone());
                }
            }}
        />
    )
}

export interface MultiSelectFilter extends AttributesMixin<typeof MultiSelectFilter> {}
@define
export class MultiSelectFilter extends FilterModel {
    static attributes = {
        ...FilterModel.attributes,
        options : [String],
        selected : type([String]).required,
        isDone : type(Boolean).check(function(x) { return x && this.selected && this.selected.length; })
    }

    delete() {
        this.$.selected.set(undefined);
        this.$.isDone.set(false);
    }

    asLabel() {
        return this.selected.length > 1 ? `Selected ${this.selected.length}` : this.selected[0];
    }

    asTitle() {
        return this.selected.map(x => x).join();
    }

    isSuccessful(value) {
        return !this.selected || !this.selected.length || this.selected.includes(value)
    }
}

export const filterByMultiSelect =  (multiSelect : MultiSelectFilter, value) : boolean => {
    return !multiSelect || !multiSelect.selected || !multiSelect.selected.length
        || multiSelect.selected.includes(value);
}